import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUITheme,
  MenuSelect,
  MenuSelectOption,
  Pagination,
} from '@equip.health/ui';

import { pageCount as pageCountOptions } from '~/lib/constants/list.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useUpdateObservationMutation } from '~/store/services/clinicalData';
import { useGetObservationVoidReasonsEnumQuery } from '~/store/services/enumerations';

import HeaderRow, { HEADER_ROW_HEIGHT } from './HeaderRow';
import KeyVitalsItem from './KeyVitalsItem';
import NewBloodPressureHeartRateKeyVitalsItem from './NewBloodPressureHeartRateKeyVitalsItem';
import NewKeyVitalsItem from './NewKeyVitalsItem';
import {
  combineHeartRateAndBloodPressureData,
  getObservationValueForProperty,
  isBloodPressureHeartRateTab,
} from './utils';
import VoidKeyVitalsModal from './VoidKeyVitalsModal';
import WeightGoals from './weight-goals';
import {
  ObservationStatusType,
  PatientVitalsTabs,
  VITALS_TABS_HEIGHT,
  VITALS_TABS_PADDING,
} from '../../constants';

const TOP_LEVEL_IDENTIFIER = 'patient__key-vitals-list';

const PAGINATION_HEIGHT = 68;
const HORIZONTAL_BORDER_HEIGHT = 1;
const LIST_HEIGHT_OFFSET =
  VITALS_TABS_HEIGHT +
  4 * VITALS_TABS_PADDING +
  HEADER_ROW_HEIGHT +
  PAGINATION_HEIGHT +
  HORIZONTAL_BORDER_HEIGHT;

type KeyVitalsListProps = {
  data: FetchPatientVitalsObservationsResponse;
  finishAddingValue: () => void;
  isAddingValue: boolean;
  onPageSizeChange: (updatedPageSize: MenuSelectOption) => void;
  pageNumber: number;
  pageSize: MenuSelectOption;
  patientId: string;
  setPageNumber: (n: number) => void;
  tab: PatientVitalsTabs;
};

const KeyVitalsList = ({
  data,
  finishAddingValue,
  isAddingValue,
  onPageSizeChange,
  pageNumber,
  pageSize,
  patientId,
  setPageNumber,
  tab,
}: KeyVitalsListProps) => {
  const { captionBold } = EquipUITheme.typography;
  const [observationToVoid, setObservationToVoid] = useState<
    PatientVitalsObservation | PatientVitalsObservation[]
  >(null);
  const handleVoidClick = (observation: PatientVitalsObservation): void =>
    setObservationToVoid(observation);

  const [observationsData, setObservationsData] =
    useState<FetchPatientVitalsObservationsResponse | null>(null);

  const [voidReasonExternalId, setVoidReasonExternalId] =
    useState<string>(null);
  const onVoidReasonSelect = (voidReasonExternalId: string): void => {
    setVoidReasonExternalId(voidReasonExternalId);
  };

  const onVoidClose = (): void => {
    setObservationToVoid(null);
    setVoidReasonExternalId(null);
  };

  const { currentUser } = useGlobalContext();

  const [
    updateObservationMutation,
    {
      isSuccess: isSuccessObservationProcessing,
      isLoading: isUpdateObservationProcessing,
      isError: errorUpdatingObservation,
    },
  ] = useUpdateObservationMutation();

  const { data: observationVoidReasons } =
    useGetObservationVoidReasonsEnumQuery();

  useEffect(() => {
    if (isSuccessObservationProcessing) onVoidClose();
  }, [isSuccessObservationProcessing]);

  useLayoutEffect(() => {
    if (data?.totalCount === 0) {
      // the data key on data (i know..) is unexpectedly missing from the BE if there are no records
      setObservationsData({
        ...data,
        data: [],
      });
    } else if (data?.data) {
      if (isBloodPressureHeartRateTab(tab)) {
        const combinedData: any[] = combineHeartRateAndBloodPressureData(
          data.data,
        );

        const groupedObservationData = {
          ...data,
          // sort based on observationDatetime
          data: combinedData.sort((a, b) => {
            if (Array.isArray(a) && !Array.isArray(b)) {
              return b?.observationDatetime?.localeCompare(
                (a[0] ?? a[1])?.observationDatetime,
              );
            }
            if (Array.isArray(b) && !Array.isArray(a)) {
              return (b[0] ?? b[1])?.observationDatetime.localeCompare(
                a?.observationDatetime,
              );
            }
            if (Array.isArray(a) && Array.isArray(b)) {
              return (b[0] ?? b[1])?.observationDatetime.localeCompare(
                (a[0] ?? a[1])?.observationDatetime,
              );
            }
            return b.observationDatetime.localeCompare(a.observationDatetime);
          }),
          pageSize: combinedData.length,
        };
        setObservationsData(groupedObservationData);
      } else {
        setObservationsData(data);
      }
    }
  }, [data]);

  const updateObservation = (options: PatientVitalsObservation): void => {
    if (options.observationExternalId) {
      // Update
      updateObservationMutation({
        ...options,
        lastUpdatedAt: new Date().toISOString(),
        updatedByUserId: currentUser.userId,
      });
    }
  };

  const onVoidConfirm = (): void => {
    if (Array.isArray(observationToVoid)) {
      observationToVoid.forEach((obsToVoid) => {
        updateObservation({
          ...obsToVoid,
          status: ObservationStatusType.VOID,
          voidReasonExternalId,
        });
      });
    } else {
      updateObservation({
        ...observationToVoid,
        status: ObservationStatusType.VOID,
        voidReasonExternalId,
      });
    }
  };

  const voidReasons = useMemo(
    () =>
      /** @TODO sorting should be handled on BE */
      [...(observationVoidReasons ?? [])]
        .sort(
          (
            { label: a }: PatientVitalsObservationVoidReason,
            { label: b }: PatientVitalsObservationVoidReason,
          ) => a.localeCompare(b),
        )
        .map(({ externalId, label }: PatientVitalsObservationVoidReason) => ({
          label,
          value: externalId,
        })),
    [observationVoidReasons],
  );

  return (
    <ChakraFlex
      direction="column"
      id={`${TOP_LEVEL_IDENTIFIER}__container`}
      justify="space-between"
      maxHeight="85vh"
      overflow="auto"
    >
      {tab === PatientVitalsTabs.WEIGHT && (
        <WeightGoals patientId={patientId} />
      )}
      <ChakraText
        {...captionBold}
        color="neutral.secondary"
        id={`${TOP_LEVEL_IDENTIFIER}__values-title`}
        marginBottom="12px"
      >
        Values
      </ChakraText>
      <VoidKeyVitalsModal
        isError={errorUpdatingObservation}
        isLoading={isUpdateObservationProcessing}
        isOpen={!!observationToVoid}
        onClose={onVoidClose}
        onConfirm={onVoidConfirm}
        onSelect={onVoidReasonSelect}
        voidReasons={voidReasons}
        voidReasonValue={voidReasonExternalId}
      />
      <ChakraBox>
        <HeaderRow tab={tab} />
        <ChakraBox
          height={`calc(100vh - ${LIST_HEIGHT_OFFSET}px)`}
          overflowY="auto"
        >
          {isAddingValue &&
            (isBloodPressureHeartRateTab(tab) ? (
              <NewBloodPressureHeartRateKeyVitalsItem
                finishAddingValue={finishAddingValue}
                patientId={patientId}
              />
            ) : (
              <NewKeyVitalsItem
                finishAddingValue={finishAddingValue}
                patientId={patientId}
                tab={tab}
              />
            ))}
          {(observationsData?.data ?? []).map(
            (obs: PatientVitalsObservation | PatientVitalsObservation[]) => (
              <KeyVitalsItem
                handleVoidClick={handleVoidClick}
                isSuccessObservationProcessing={isSuccessObservationProcessing}
                isUpdateObservationProcessing={isUpdateObservationProcessing}
                key={`${TOP_LEVEL_IDENTIFIER}-item__${getObservationValueForProperty(
                  obs,
                  'observationExternalId',
                )}`}
                observations={obs}
                tab={tab}
                updateObservation={updateObservation}
                updateObservationError={errorUpdatingObservation}
              />
            ),
          )}
        </ChakraBox>
      </ChakraBox>

      {(data?.totalCount ?? 0) > 0 && (
        <ChakraFlex width="100%">
          <ChakraBox marginTop="10px">
            <MenuSelect
              id={`${TOP_LEVEL_IDENTIFIER}__page-size`}
              onSelect={onPageSizeChange}
              options={pageCountOptions}
              selectedValues={pageSize}
            />
          </ChakraBox>
          <ChakraBox flexGrow={1}>
            <Pagination
              current={pageNumber}
              onPageSelect={setPageNumber}
              total={Math.ceil(
                (data?.totalCount ?? 0) / Number(pageSize.value),
              )}
            />
          </ChakraBox>
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default KeyVitalsList;
